import { Col, Row, Typography } from "antd";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import { ClearFilters } from "./components/ClearFilters";
import { Facets } from "./components/Facets";
import { PrimaryControls } from "./components/PrimaryControls";
import { SearchResults } from "./components/SearchResults";
import { GUTTER_MEDIUM, QUERYSTATE_INITIAL_STATE } from "./constants";
import { queryReducer } from "./queryReducer";
import { AppletProps, QueryState } from "./types";
import { buildQueryParams, buildSearchParamsFromState, extractSearchParamsFromAttributes } from "./utils";

import { AppletWrapper } from "~/Atom/AppletWrapper";
import { isDevelopment } from "~/env";
import { useFlag } from "~/LaunchDarkly";
import { useOmnichannelApi } from "~/OmnichannelApi";
import { Paths } from "~/OmnichannelApi/openapi";
import { isTruthy } from "~/utils";

const Applet: React.FC<AppletProps> = ({
  inlineSearchParams,
  searchParams,
  header: showHeader,
  headerLevel,
  instantSearch: isInstantSearch,
  primaryControls: showPrimaryControls,
  facets: showFacets,
  clearAllOption,
  searchResults: showSearchResults,
  headerText,
  locationSlug,
}) => {
  const api = useOmnichannelApi();
  const hasAuthorization = useFlag("applet-es-job-board");

  // State management - group related state variables
  const [jobs, setJobs] = useState<Paths.JobsList.Responses.$200["jobs"]>([]);
  const [metadata, setMetaData] = useState<Paths.JobsList.Responses.$200["metadata"]>();
  const [facets, setFacets] = useState<Paths.JobsList.Responses.$200["metadata"]["facets"]>();

  // UI state
  const [isLoading, setIsLoading] = useState(true);

  // Refs for tracking render state
  const isFirstRender = useRef(true);

  // Query state management
  const [queryState, queryDispatch] = useReducer<typeof queryReducer>(queryReducer, {
    ...QUERYSTATE_INITIAL_STATE,
    ...(inlineSearchParams || searchParams),
    commuteFilter: Boolean(searchParams.commuteFilter === "true"),
    locationSlug: locationSlug || QUERYSTATE_INITIAL_STATE.locationSlug,
  });

  const executeJobSearch = (queryState: QueryState) => {
    setJobs([])
    setMetaData(undefined);
    setIsLoading(true);

    const queryParams: Paths.JobsList.QueryParameters & Paths.JobsList.HeaderParameters = buildQueryParams(queryState);

    api["jobs.list"](queryParams)
      .then((res) => {
        if (setFacets) {
          setFacets(res.data.metadata.facets);
        }
        setJobs(res.data.jobs);
        setMetaData(res.data.metadata);

        const searchParams = buildSearchParamsFromState(queryState);

        if (!inlineSearchParams && !locationSlug) {
          window.history.replaceState(
            null,
            "",
            [window.location.pathname, searchParams.toString() || undefined].filter(isTruthy).join("?")
          );
        }
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const showMoreJobs = () => {
    if (metadata?.nextPageToken) {
      const queryParams: Paths.JobsList.QueryParameters & Paths.JobsList.HeaderParameters = buildQueryParams(
        queryState,
        { pageToken: metadata.nextPageToken }
      );

      setIsLoading(true);
      api["jobs.list"](queryParams)
        .then((res) => {
          setJobs((prevJobs) => [...prevJobs, ...res.data.jobs]);
          setMetaData(res.data.metadata);
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const initiateSearch = (queryState: QueryState) => {
    if (!isInstantSearch && !showSearchResults) {
      const params = buildSearchParamsFromState(queryState);
      const queryString = params.toString();
      const url = `/careers/current-openings${queryString ? `?${queryString}` : ""}`;
      window.location.href = url;
    } else {
      executeJobSearch(queryState);
    }
  };

  useEffect(() => {
    if (isInstantSearch || isFirstRender.current) {
      isFirstRender.current = false;
      showSearchResults && initiateSearch(queryState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("unavailable") === "true") {
      // eliminate race condition on applet mounting
      setTimeout(() => {
        window.notifyUser &&
          window.notifyUser("error", {
            message: "The opening you requested is no longer available.",
            duration: 15,
          });
      }, 500);
    }
  }, []);

  if (hasAuthorization !== true) {
    if (isDevelopment) {
      console.error("Unauthorized to access applet.");
    }

    return null;
  }

  return (
    <AppletWrapper appletName="job-board">
      <div
        style={{
          margin: "auto",
          maxWidth: "90rem",
        }}
      >
        {showHeader && (
          <Row gutter={GUTTER_MEDIUM}>
            <Col span={24}>
              <Typography.Title level={headerLevel}>
                {headerText}
              </Typography.Title>
            </Col>
          </Row>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {showPrimaryControls && (
            <PrimaryControls
              queryState={queryState}
              queryDispatch={queryDispatch}
              instantSearch={isInstantSearch}
              onSearchClick={() => initiateSearch(queryState)}
            />
          )}

          {showFacets && (
            <Facets
              queryState={queryState}
              queryDispatch={queryDispatch}
              facets={facets}
            />
          )}

          {
            clearAllOption && isInstantSearch && (
              <ClearFilters  queryDispatch={queryDispatch}  />
            )
          }
        </div>

        {showSearchResults && (
          <SearchResults
            jobs={jobs}
            metadata={metadata}
            isLoading={isLoading}
            shouldUpdateUrlSearchParams={!inlineSearchParams && !locationSlug}
            queryState={queryState}
            queryDispatch={queryDispatch}
            showMoreJobs={showMoreJobs}
          />
        )}
      </div>
    </AppletWrapper>
  );
};

export const mount = (el: HTMLDivElement) => {
  const root = createRoot(el);
  const props = extractSearchParamsFromAttributes(el);
  root.render(<Applet {...props} />);
};
